import { useCallback } from "react";
import {
  AUTH_ACCOUNTS,
  AUTH_STORAGE_KEY,
  AUTH_USER,
  CSRFTOKEN,
  INCLUDE_ACCOUNTS,
  INCLUDE_ALL_ACCOUNTS,
  NOTIFICATIONS,
  TICKETS_COUNTS,
} from "@common/constants";
import { AuthenticatedUser } from "../types";
import {
  getEventKey,
  useLocalStorage,
} from "@services/common/hooks/useLocalStorage";
import { Account } from "@services/account/types";
import { isHomeAccountOnly } from "@common/utils/user";

export const AUTH_EVENT_KEY = getEventKey(AUTH_USER);
// For a gain in performance, dispatch the new AuthenticatedUser value through the Event in itself
export function useAuthenticatedUser(): [
  user: undefined | AuthenticatedUser,
  setAuthenticatedUser: (user: null | AuthenticatedUser) => void
] {
  const [user, setUser] = useLocalStorage<AuthenticatedUser>(AUTH_USER);

  const setPersistentUser = useCallback(
    (user?: null | AuthenticatedUser) => {
      setUser(user);
    },
    [setUser]
  );

  return [user ?? undefined, setPersistentUser];
}

export const cleanAuthenticatedUser = () => {
  [
    AUTH_STORAGE_KEY,
    AUTH_ACCOUNTS,
    AUTH_USER,
    NOTIFICATIONS,
    CSRFTOKEN,
    TICKETS_COUNTS,
  ].forEach((key) => localStorage.removeItem(key));
  dispatchEvent(new Event(AUTH_EVENT_KEY));
};

export const setIncludedAccounts = (
  user: AuthenticatedUser,
  accounts: Account[]
) => {
  // in OLD Symphony, filter by account storage was done per userId so that 2 persons could log in on the same machine and see their own filtered accounts
  // in real life, maybe it doesn't make sense
  if (isHomeAccountOnly(user)) {
    localStorage.setItem(`${INCLUDE_ALL_ACCOUNTS}_${user.id}`, "false");
    localStorage.setItem(
      `${INCLUDE_ACCOUNTS}_${user.id}`,
      JSON.stringify([user.account.id])
    );
    // Don't override if there's already an existing selection of accounts
  } else if (!localStorage.getItem(`${INCLUDE_ALL_ACCOUNTS}_${user.id}`)) {
    localStorage.setItem(`${INCLUDE_ALL_ACCOUNTS}_${user.id}`, "true");
    localStorage.setItem(
      `${INCLUDE_ACCOUNTS}_${user.id}`,
      JSON.stringify(accounts.map((a) => a.id))
    );
  }
};
